'use client';

import React, { useState, useEffect } from 'react';

// ─── Data ─────────────────────────────────────────────────────────────────────

export interface IndustryVertical {
  id: string;
  name: string;
  shortDesc: string;
  iconPath: string;
  commonApps: string[];
  retirementDrivers: string[];
  compliance: string[];
  operationalBenefits: string[];
}

const INDUSTRIES: IndustryVertical[] = [
  {
    id: 'industry-healthcare',
    name: 'Healthcare',
    shortDesc: 'Clinical, imaging, laboratory, revenue-cycle, workforce, ERP, and departmental systems across hospital networks and health systems.',
    iconPath: 'M4.5 12.75l6 6 9-13.5',
    commonApps: [
      'MEDITECH MAGIC',
      'Epic/Cerner legacy modules',
      'Allscripts/Veradigm Sunrise',
      'GE Centricity PACS',
      'Sunquest LIS',
      'Infor Lawson',
      'Kronos/UKG',
      'McKesson supply chain',
    ],
    retirementDrivers: [
      'EHR/ERP consolidation post-M&A',
      'Cloud/SaaS migration initiatives',
      'Vendor end-of-support for legacy platforms',
      'PHI/PII cybersecurity risk reduction',
      '21st Century Cures Act information blocking provisions',
      'Interoperability mandates requiring HL7 FHIR adoption',
    ],
    compliance: [
      'HIPAA Privacy and Security Rules — data retention and access controls',
      'HITECH Act — breach notification and audit trail requirements',
      '21st Century Cures Act — interoperability and information blocking',
      'CMS Conditions of Participation — medical record retention',
      '42 CFR Part 2 — substance use disorder records',
      'State health-record retention laws (vary by jurisdiction)',
    ],
    operationalBenefits: [
      'Reduced PHI exposure risk through controlled data archiving',
      'Lower infrastructure and licensing costs for legacy platforms',
      'Indexed historical access for audits and legal requests',
      'Faster audit response with structured historical data access',
      'Improved interoperability with modern EHR ecosystems',
    ],
  },
  {
    id: 'industry-banking',
    name: 'Banking & Financial Services',
    shortDesc: 'Core banking, lending, payments, capital markets, wealth, CRM, and back-office platforms across retail and investment banking.',
    iconPath: 'M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z',
    commonApps: [
      'IBM z/OS mainframe / COBOL applications',
      'Hogan core banking',
      'FIS Systematics',
      'Jack Henry SilverLake',
      'Temenos T24 legacy versions',
      'BASE24 payment processing',
      'Calypso / Murex capital markets',
      'Siebel CRM / SAP ECC',
    ],
    retirementDrivers: [
      'Core banking modernization and cloud-native migration',
      'Cloud/data-center exit initiatives',
      'Mainframe cost reduction programs',
      'Merger-driven system duplication elimination',
      'Cybersecurity risk from aging, unsupported platforms',
      'Open banking API mandates requiring modern integration layers',
    ],
    compliance: [
      'GLBA — financial records and customer data protection',
      'FTC Safeguards Rule — information security program requirements',
      'SEC/FINRA books-and-records requirements',
      'Regulation S-P — privacy of consumer financial information',
      'BSA/AML — transaction monitoring and KYC documentation',
      'State privacy laws — data retention and breach notification',
    ],
    operationalBenefits: [
      'Reduced mainframe and middleware licensing costs',
      'Centralized historical records for audit and regulator readiness',
      'Improved audit and regulatory examination response',
      'Faster product launches with reduced legacy integration constraints',
      'Streamlined regulatory reporting with modern data architecture',
    ],
  },
  {
    id: 'industry-manufacturing',
    name: 'Manufacturing',
    shortDesc: 'ERP, MRP, MES, SCADA, historians, quality, maintenance, PLM, and plant-floor systems across discrete and process manufacturing.',
    iconPath: 'M11.42 15.17L17.25 21A2.652 2.652 0 0021 17.25l-5.877-5.877M11.42 15.17l2.496-3.03c.317-.384.74-.626 1.208-.766M11.42 15.17l-4.655 5.653a2.548 2.548 0 11-3.586-3.586l6.837-5.63m5.108-.233c.55-.164 1.163-.188 1.743-.14a4.5 4.5 0 004.486-6.336l-3.276 3.277a3.004 3.004 0 01-2.25-2.25l3.276-3.276a4.5 4.5 0 00-6.336 4.486c.091 1.076-.071 2.264-.904 2.95l-.102.085m-1.745 1.437L5.909 7.5H4.5L2.25 3.75l1.5-1.5L7.5 4.5v1.409l4.26 4.26m-1.745 1.437l1.745-1.437m6.615 8.206L15.75 15.75M4.867 19.125h.008v.008h-.008v-.008z',
    commonApps: [
      'SAP ECC / JD Edwards / Oracle EBS',
      'Infor LN / BPCS / M3',
      'Siemens SIMATIC IT (MES)',
      'GE Proficy / OSIsoft PI (historians)',
      'LabWare LIMS',
      'Siemens Teamcenter / PTC Windchill (PLM)',
      'Legacy SCADA and Distributed Control Systems (DCS)',
      'Legacy shop floor data collection systems',
    ],
    retirementDrivers: [
      'SAP S/4HANA migration from SAP ECC',
      'Plant consolidation and divestiture programs',
      'Aging OT hardware with no vendor support',
      'IT/OT cybersecurity risk reduction',
      'Industry 4.0 and smart manufacturing initiatives',
      'Carbon reporting and sustainability data requirements',
    ],
    compliance: [
      'OSHA recordkeeping — safety incidents and worker exposure records',
      'Product traceability and recall — batch records and genealogy',
      'FDA 21 CFR Part 11 — electronic records for regulated manufacturers',
      'EPA environmental compliance — emissions and waste reporting',
      'Export controls — ITAR/EAR record retention',
      'Product liability retention — batch records and traceability',
    ],
    operationalBenefits: [
      'Production history retention post-ERP replacement',
      'Reduced plant infrastructure and licensing costs',
      'Improved batch and lot auditability for recalls and investigations',
      'Faster warranty claim resolution with structured historical data',
      'Real-time production visibility through modern MES integration',
    ],
  },
  {
    id: 'industry-retail',
    name: 'Retail',
    shortDesc: 'POS, merchandising, pricing, inventory, loyalty, e-commerce, OMS, warehouse, and ERP across omnichannel retail.',
    iconPath: 'M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25a3 3 0 00-3 3h15.75m-12.75-3h11.218c1.121-2.3 2.1-4.684 2.924-7.138a60.114 60.114 0 00-16.536-1.84M7.5 14.25L5.106 5.272M6 20.25a.75.75 0 11-1.5 0 .75.75 0 011.5 0zm12.75 0a.75.75 0 11-1.5 0 .75.75 0 011.5 0z',
    commonApps: [
      'IBM 4690 POS',
      'Oracle Retail / Retek',
      'Manhattan WMS',
      'IBM Sterling OMS',
      'IBM WebSphere Commerce',
      'Siebel Loyalty',
      'SAP ECC',
      'JDA / Blue Yonder legacy versions',
    ],
    retirementDrivers: [
      'POS refresh and omnichannel modernization',
      'Store closures and portfolio rationalization',
      'Duplicate payment and customer system consolidation',
      'Payment security and PCI DSS scope reduction',
      'Cloud-native commerce adoption (Salesforce Commerce, Shopify Plus)',
      'Customer data unification and CDP implementation',
    ],
    compliance: [
      'PCI DSS — payment card data retention and security',
      'State consumer-privacy and breach-notification laws',
      'Sales-tax recordkeeping requirements',
      'Employment records and wage documentation',
      'GDPR — customer personal data retention and consent (EU)',
      'CCPA/CPRA — consumer data rights and deletion (California)',
    ],
    operationalBenefits: [
      'Reduced PCI scope through legacy retirement',
      'Lower store and server infrastructure costs',
      'Historical transaction and loyalty data access post-migration',
      'Simplified merchandising with modern commerce platforms',
      'Unified customer view enabling personalized engagement',
    ],
  },
  {
    id: 'industry-insurance',
    name: 'Insurance',
    shortDesc: 'Policy administration, claims, billing, actuarial, document, agent, and mainframe systems across P&C and life insurance.',
    iconPath: 'M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z',
    commonApps: [
      'IBM mainframe / COBOL policy systems',
      'DXC/CSC Life70 / CyberLife / Vantage-One',
      'Guidewire older releases',
      'Duck Creek legacy versions',
      'Majesco / Sapiens legacy platforms',
      'Siebel CRM',
      'Oracle EBS / PeopleSoft',
      'Legacy actuarial and document management systems',
    ],
    retirementDrivers: [
      'Policy and claims platform modernization',
      'Carrier acquisitions requiring system consolidation',
      'Mainframe cost reduction programs',
      'Closed books requiring inquiry-only access',
      'Long-tail claims requiring historical data retention',
      'AI-driven underwriting and claims automation adoption',
    ],
    compliance: [
      'State insurance department requirements — policy and claims records',
      'NAIC cybersecurity model law compliance',
      'HIPAA — health plan data retention and security',
      'GLBA — policyholder financial data protection',
      'Market-conduct rules — claims and correspondence records',
      'AML/KYC requirements — policyholder identity records',
    ],
    operationalBenefits: [
      'Archived closed-policy and claims history for inquiry access',
      'Reduced mainframe licensing and infrastructure costs',
      'Improved regulatory examination response',
      'Consolidated acquired books of business',
      'Faster claims processing and settlement through automation',
    ],
  },
  {
    id: 'industry-government',
    name: 'Government / Public Sector',
    shortDesc: 'Citizen services, tax, benefits, licensing, grants, pensions, case management, GIS, and mainframe databases in public sector.',
    iconPath: 'M12 21v-8.25M15.75 21v-8.25M8.25 21v-8.25M3 9l9-6 9 6m-1.5 12V10.332A48.36 48.36 0 0012 9.75c-2.551 0-5.056.2-7.5.582V21M3 21h18M12 6.75h.008v.008H12V6.75z',
    commonApps: [
      'IBM z/OS / COBOL / CICS mainframe systems',
      'Unisys ClearPath applications',
      'PeopleSoft / CGI Advantage',
      'IBM FileNet / Lotus Notes / Domino',
      'Esri ArcGIS Desktop / ArcMap',
      'Accela permitting and licensing',
      'Legacy citizen service portals and e-governance platforms',
      'Mainframe-based tax assessment and collection systems',
    ],
    retirementDrivers: [
      'Digital-service modernization programs',
      'Data-center closures and cloud adoption',
      'Aging mainframe workforce and skills gap',
      'Agency reorganizations and consolidations',
      'Zero-trust cybersecurity initiatives',
      'Budget rationalization and IT consolidation directives',
    ],
    compliance: [
      'Federal Records Act / NARA — official records preservation',
      'FOIA / e-discovery — records availability for public information requests',
      'FISMA — federal information security requirements',
      'Privacy Act — citizen data retention and processing',
      'State and local public-records retention schedules',
      'CAG / audit requirements — financial and administrative records',
    ],
    operationalBenefits: [
      'Preserved official records through system transitions',
      'Faster FOIA and audit retrieval with structured data archives',
      'Reduced duplicated citizen data across agencies',
      'Improved security posture through legacy retirement',
      'Lower total cost of ownership through infrastructure consolidation',
    ],
  },
  {
    id: 'industry-education',
    name: 'Education',
    shortDesc: 'Student information, LMS, finance, HR, research, library, admissions, and advancement systems across higher education.',
    iconPath: 'M4.26 10.147a60.436 60.436 0 00-.491 6.347A48.627 48.627 0 0112 20.904a48.627 48.627 0 018.232-4.41 60.46 60.46 0 00-.491-6.347m-15.482 0a50.57 50.57 0 00-2.658-.813A59.905 59.905 0 0112 3.493a59.902 59.902 0 0110.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.697 50.697 0 0112 13.489a50.702 50.702 0 017.74-3.342M6.75 15a.75.75 0 100-1.5.75.75 0 000 1.5zm0 0v-3.675A55.378 55.378 0 0112 8.443m-7.007 11.55A5.981 5.981 0 006.75 15.75v-1.5',
    commonApps: [
      'Ellucian Banner / Colleague (SIS)',
      'PeopleSoft Campus Solutions',
      'Blackboard Learn / Moodle (LMS)',
      'Ex Libris Aleph / Voyager (library)',
      'Blackbaud / Raisers Edge (advancement)',
      'Legacy alumni and donor management platforms',
      'Older financial aid and scholarship management systems',
      'Legacy research data management platforms',
    ],
    retirementDrivers: [
      'SIS and LMS modernization (Canvas, Blackboard Ultra)',
      'Campus data-center consolidation',
      'Institution mergers and system deduplication',
      'Aging integrations requiring modern API architecture',
      'Transcript and research history retention requirements',
      'Accreditation requirements for data reporting and outcomes tracking',
    ],
    compliance: [
      'FERPA — student education records retention and privacy (US)',
      'COPPA — children\'s online privacy protection',
      'GLBA Safeguards Rule — student financial data protection',
      'Research / grant / IRB retention — funding agency requirements',
      'State public-records retention schedules',
      'Financial aid records — federal and state retention mandates',
    ],
    operationalBenefits: [
      'Preserved transcripts post-SIS replacement',
      'Reduced campus server and infrastructure costs',
      'Consolidated departmental databases across merged institutions',
      'Reduced student and employee PII exposure',
      'Streamlined accreditation reporting with structured data access',
    ],
  },
  {
    id: 'industry-energy',
    name: 'Energy & Utilities',
    shortDesc: 'GIS, CIS/billing, SCADA/DCS, OMS/DMS/EMS, meter, EAM, historian, work management, and ERP across power and utilities.',
    iconPath: 'M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z',
    commonApps: [
      'Esri ArcGIS Desktop / GE Smallworld (GIS)',
      'Oracle Utilities CC&B / SAP IS-U (billing/CIS)',
      'GE iFIX / Wonderware/AVEVA (SCADA)',
      'Itron MV-90 (metering)',
      'IBM Maximo (EAM)',
      'OSIsoft PI (historian)',
      'SAP ECC (ERP)',
      'Legacy outage and energy management systems (OMS/EMS)',
    ],
    retirementDrivers: [
      'GIS / AMI / MDMS modernization programs',
      'Grid modernization and smart grid adoption',
      'CIS and billing transformation initiatives',
      'SCADA refresh and OT cybersecurity mandates',
      'Cloud consolidation and data-center exit',
      'Carbon neutrality commitments driving operational transformation',
    ],
    compliance: [
      'NERC CIP — critical infrastructure protection for bulk electric systems',
      'FERC / state PUC requirements — energy market records retention',
      'PHMSA pipeline safety regulations',
      'EPA Safe Drinking Water / Clean Water Act compliance',
      'CEA regulations — grid safety and data retention (India)',
      'ISO 55001 — asset management records and audit requirements',
    ],
    operationalBenefits: [
      'Preserved GIS topology and asset history through modernization',
      'Reduced SCADA / CIS / billing infrastructure risk',
      'Faster outage and meter evidence access for investigations',
      'Improved grid reliability through modern monitoring and analytics',
      'Predictive maintenance reducing unplanned outage frequency',
    ],
  },
  {
    id: 'industry-telecom',
    name: 'Telecommunications',
    shortDesc: 'BSS/OSS, billing, CRM, mediation, provisioning, network management, and inventory systems across wireline and wireless carriers.',
    iconPath: 'M8.288 15.038a5.25 5.25 0 017.424 0M5.106 11.856c3.807-3.808 9.98-3.808 13.788 0M1.924 8.674c5.565-5.565 14.587-5.565 20.152 0M12.53 18.22l-.53.53-.53-.53a.75.75 0 011.06 0z',
    commonApps: [
      'Amdocs legacy billing platforms',
      'Oracle Communications BRM',
      'Ericsson BSCS',
      'CSG billing systems',
      'Netcracker OSS',
      'Siebel Communications CRM',
      'HP OpenView / IBM Tivoli / Netcool (NMS)',
      'Legacy mediation and interconnect billing systems',
    ],
    retirementDrivers: [
      '5G / fiber / cloud-native network transformation',
      'BSS/OSS post-merger consolidation',
      'Billing modernization and digital-first service delivery',
      'Copper and circuit-switched network retirement',
      'Customer experience modernization requirements',
      'Regulatory pressure for real-time reporting and lawful intercept',
    ],
    compliance: [
      'FCC CPNI requirements — customer proprietary network information',
      'CALEA — lawful intercept and CDR retention',
      '911 / E911 records retention requirements',
      'FCC / state consumer-protection regulations',
      'PCI DSS — payment card data security',
      'GDPR — subscriber data retention and consent (EU)',
    ],
    operationalBenefits: [
      'Reduced BSS/OSS complexity through legacy platform retirement',
      'Preserved billing / CDR / provisioning history post-migration',
      'Improved regulator and dispute response capabilities',
      'Faster service provisioning through modern order management',
      'Lower OSS/BSS licensing and integration maintenance costs',
    ],
  },
  {
    id: 'industry-financial-services',
    name: 'Financial Services',
    shortDesc: 'Legacy wealth management, payment processing, fund administration, and compliance platforms across financial services firms.',
    iconPath: 'M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
    commonApps: [
      'PeopleSoft / Oracle EBS / SAP ECC',
      'IBM AS/400 / IBM i applications',
      'IBM mainframe / COBOL applications',
      'FIS / Fiserv legacy platforms',
      'Sungard applications',
      'Calypso / Murex (capital markets)',
      'Siebel CRM',
      'Legacy fund administration and compliance platforms',
    ],
    retirementDrivers: [
      'Digital transformation and cloud-native platform adoption',
      'Open finance and API-driven ecosystem requirements',
      'Regulatory modernization (MiFID II, EMIR, Dodd-Frank reporting)',
      'Real-time payments infrastructure (UPI, SWIFT gpi, ISO 20022)',
      'AI-driven risk management and compliance automation',
      'Vendor end-of-life for legacy financial middleware',
    ],
    compliance: [
      'SEBI regulations — investment records and audit trails (India)',
      'MiFID II — trade reporting and record retention (EU)',
      'Dodd-Frank Act — derivatives and swap data retention (US)',
      'FATF AML/CFT — transaction monitoring and KYC records',
      'SOX — financial records and internal controls documentation',
      'GDPR — investor data retention and consent (EU)',
    ],
    operationalBenefits: [
      'Faster regulatory reporting with modern data architecture',
      'Reduced operational risk from unsupported legacy platforms',
      'Improved client experience through digital-first service delivery',
      'Lower infrastructure and licensing costs for retired systems',
      'Enhanced fraud detection through modern analytics capabilities',
    ],
  },
  {
    id: 'industry-food-beverages',
    name: 'Food & Beverages',
    shortDesc: 'Process ERP, recipe/batch, MES, quality/LIMS, warehouse, maintenance, and traceability systems across food and beverage manufacturers.',
    iconPath: 'M12 8.25v-1.5m0 1.5c-1.355 0-2.697.056-4.024.166C6.845 8.51 6 9.473 6 10.608v2.513m6-4.871c1.355 0 2.697.056 4.024.166C17.155 8.51 18 9.473 18 10.608v2.513M15 8.25v-1.5m-6 1.5v-1.5m12 9.75-1.5.75a3.354 3.354 0 01-3 0 3.354 3.354 0 00-3 0 3.354 3.354 0 01-3 0 3.354 3.354 0 00-3 0 3.354 3.354 0 01-3 0L3 18m0-13.5h18',
    commonApps: [
      'SAP ECC / JD Edwards / Oracle EBS',
      'Infor M3 / LX / XA',
      'QAD legacy versions',
      'GE Proficy / Rockwell FactoryTalk (MES)',
      'LabWare LIMS',
      'Manhattan WMS',
      'IBM Maximo (maintenance)',
      'OSIsoft PI (historian)',
    ],
    retirementDrivers: [
      'ERP and plant standardization post-acquisition',
      'Cloud ERP and MES migration initiatives',
      'Food-safety traceability mandate compliance (FSMA, FSSAI)',
      'Aging plant hardware with no vendor support',
      'Sustainability reporting and carbon footprint tracking',
      'Consumer transparency demands for ingredient and origin data',
    ],
    compliance: [
      'FDA FSMA — food traceability and supply chain records (US)',
      'FDA 21 CFR Part 11 — electronic records for regulated manufacturers',
      'USDA / FSIS — food safety and inspection records',
      'HACCP documentation — hazard analysis and critical control records',
      'OSHA / EPA — safety and environmental compliance',
      'Product liability — batch and quality records retention',
    ],
    operationalBenefits: [
      'Preserved recipe, batch, quality, and recall history',
      'Lower plant infrastructure and licensing costs',
      'Accelerated traceability investigations and product recalls',
      'Reduced waste through real-time inventory and demand visibility',
      'Improved quality consistency via modern recipe management',
    ],
  },
  {
    id: 'industry-hospitality',
    name: 'Hospitality',
    shortDesc: 'PMS, CRS, POS, sales/catering, loyalty, finance, and workforce systems across hotels and hospitality groups.',
    iconPath: 'M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25',
    commonApps: [
      'Oracle Hospitality OPERA / MICROS-Fidelio',
      'Infor HMS',
      'Agilysys legacy applications',
      'MICROS 3700 / 9700 POS',
      'Sabre SynXis / Amadeus Delphi (CRS/catering)',
      'Siebel Hospitality CRM',
      'Kronos / UKG workforce management',
      'Custom hotel reservation systems',
    ],
    retirementDrivers: [
      'PMS and CRS cloud modernization (Opera Cloud, Mews)',
      'Brand and franchise consolidation post-acquisition',
      'MICROS and POS system replacement',
      'Property-level server reduction programs',
      'Guest experience personalization requiring unified data platforms',
      'Post-pandemic operational restructuring and cost optimization',
    ],
    compliance: [
      'PCI DSS — payment card data retention and security',
      'State consumer-privacy laws — guest data retention',
      'Hotel occupancy and lodging tax records',
      'Employment and payroll records retention',
      'Food and alcohol service records — regulatory requirements',
      'Tourism regulatory requirements — guest registration records',
    ],
    operationalBenefits: [
      'Preserved guest and folio history post-PMS replacement',
      'Reduced property-level servers and infrastructure costs',
      'Consolidated records across acquired properties',
      'Enhanced guest experience through personalized service delivery',
      'Improved revenue per available room (RevPAR) via modern RMS',
    ],
  },
  {
    id: 'industry-management-consulting',
    name: 'Management Consulting',
    shortDesc: 'ERP, PSA, project accounting, time/expense, CRM, document, knowledge, and collaboration systems across consulting firms.',
    iconPath: 'M20.25 14.15v4.25c0 1.094-.787 2.036-1.872 2.18-2.087.277-4.216.42-6.378.42s-4.291-.143-6.378-.42c-1.085-.144-1.872-1.086-1.872-2.18v-4.25m16.5 0a2.18 2.18 0 00.75-1.661V8.706c0-1.081-.768-2.015-1.837-2.175a48.114 48.114 0 00-3.413-.387m4.5 8.006c-.194.165-.42.295-.673.38A23.978 23.978 0 0112 15.75c-2.648 0-5.195-.429-7.577-1.22a2.016 2.016 0 01-.673-.38m0 0A2.18 2.18 0 013 12.489V8.706c0-1.081.768-2.015 1.837-2.175a48.111 48.111 0 013.413-.387m7.5 0V5.25A2.25 2.25 0 0013.5 3h-3a2.25 2.25 0 00-2.25 2.25v.894m7.5 0a48.667 48.667 0 00-7.5 0M12 12.75h.008v.008H12v-.008z',
    commonApps: [
      'PeopleSoft / Oracle EBS / SAP ECC',
      'Deltek Costpoint / Vision (PSA)',
      'CA Clarity PPM',
      'Siebel CRM',
      'Lotus Notes / Domino',
      'SharePoint 2010 / 2013',
      'BMC Remedy',
      'Custom time and billing systems',
    ],
    retirementDrivers: [
      'ERP and PSA post-acquisition consolidation',
      'Move to Microsoft 365 and SaaS platforms',
      'Fragmented internal systems rationalization',
      'Sensitive client-data exposure risk reduction',
      'Knowledge management modernization for AI-assisted insights',
      'Remote and hybrid workforce collaboration platform adoption',
    ],
    compliance: [
      'Client contractual confidentiality and retention obligations',
      'State privacy laws — client and employee data retention',
      'SOX — financial records for publicly listed consulting firms',
      'Employment, payroll, and immigration records retention',
      'Legal and professional-liability holds',
      'Tax authority requirements — billing and revenue records',
    ],
    operationalBenefits: [
      'Preserved project and client history without old PSA/ERP',
      'Reduced license overlap from duplicate systems',
      'Simplified merger integration and system consolidation',
      'Improved knowledge reuse through modern AI-powered repositories',
      'Better resource utilization via real-time capacity visibility',
    ],
  },
  {
    id: 'industry-restaurants',
    name: 'Restaurants',
    shortDesc: 'POS, back-office, labor, inventory, loyalty, ordering, accounting, and kitchen systems across restaurant chains and QSR operators.',
    iconPath: 'M12 8.25v-1.5m0 1.5c-1.355 0-2.697.056-4.024.166C6.845 8.51 6 9.473 6 10.608v2.513m6-4.871c1.355 0 2.697.056 4.024.166C17.155 8.51 18 9.473 18 10.608v2.513M15 8.25v-1.5m-6 1.5v-1.5m12 9.75-1.5.75a3.354 3.354 0 01-3 0 3.354 3.354 0 00-3 0 3.354 3.354 0 01-3 0 3.354 3.354 0 00-3 0 3.354 3.354 0 01-3 0L3 18m0-13.5h18',
    commonApps: [
      'Oracle MICROS 3700 / 9700 POS',
      'NCR Aloha',
      'Compeat back-office',
      'HotSchedules labor management',
      'Kronos Workforce Central',
      'Crunchtime inventory management',
      'Legacy loyalty and gift-card databases',
      'Custom restaurant and back-office applications',
    ],
    retirementDrivers: [
      'POS refresh and cloud ordering/loyalty/labor modernization',
      'Franchise acquisitions requiring system consolidation',
      'Store-level server cost reduction',
      'Payment security and PCI DSS compliance',
      'Digital ordering and delivery platform integration requirements',
      'Ghost kitchen and virtual brand operational requirements',
    ],
    compliance: [
      'PCI DSS — payment card data security and retention',
      'FLSA — wage, hour, and tip records retention',
      'Food-safety and health-department requirements',
      'Sales tax and transaction records retention',
      'State privacy and breach-notification laws',
      'Allergen labelling regulations — ingredient and recipe records',
    ],
    operationalBenefits: [
      'Preserved transaction and labor history post-POS replacement',
      'Reduced PCI and privacy exposure through legacy retirement',
      'Consolidated loyalty data across brands',
      'Faster order fulfilment through modern KDS and POS integration',
      'Improved food cost control via real-time recipe and inventory data',
    ],
  },
  {
    id: 'industry-real-estate',
    name: 'Real Estate',
    shortDesc: 'Property management, lease accounting, facilities, construction, valuation, CRM, and finance systems across commercial and residential real estate.',
    iconPath: 'M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25',
    commonApps: [
      'Yardi Voyager legacy versions',
      'MRI Software legacy platforms',
      'RealPage OneSite',
      'JD Edwards / ARGUS DCF / Enterprise',
      'IBM Maximo (facilities)',
      'Microsoft Dynamics CRM',
      'OpenText Documentum',
      'Esri ArcGIS (GIS/mapping)',
    ],
    retirementDrivers: [
      'Portfolio acquisitions and dispositions',
      'Cloud PMS and lease-accounting modernization',
      'ERP standardization across acquired portfolios',
      'Legacy document and maintenance system replacement',
      'ESG reporting and building sustainability data requirements',
      'Smart building IoT integration requiring modern data platforms',
    ],
    compliance: [
      'State real-estate brokerage and transaction records requirements',
      'Fair Housing Act — tenant and applicant records',
      'RESPA / TILA — mortgage and transaction documentation',
      'Tax and financial-audit records retention',
      'State privacy and breach-notification laws',
      'Anti-money laundering — transaction and beneficial ownership records',
    ],
    operationalBenefits: [
      'Preserved lease and property history through portfolio changes',
      'Reduced duplicate platforms post-acquisition',
      'Centralized historical valuation evidence',
      'Improved tenant satisfaction through modern digital portals',
      'Faster lease abstraction and portfolio reporting capabilities',
    ],
  },
  {
    id: 'industry-airlines',
    name: 'Airlines / Aviation',
    shortDesc: 'Reservations, passenger service, flight operations, crew, revenue accounting, MRO/maintenance, cargo, and mainframe systems.',
    iconPath: 'M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5',
    commonApps: [
      'Sabre / SabreSonic',
      'Amadeus Altéa',
      'Travelport Galileo / Worldspan',
      'IBM z/OS TPF / zTPF mainframe',
      'Jeppesen crew and flight planning',
      'TRAX / Swiss-AS AMOS (MRO)',
      'SAP ECC (ERP)',
      'Custom crew and flight operations systems',
    ],
    retirementDrivers: [
      'PSS and airline retail modernization (NDC)',
      'Mainframe workload cloud migration',
      'Airline mergers requiring system consolidation',
      'MRO and crew system upgrades',
      'Sustainability reporting and carbon offset tracking',
      'Passenger experience modernization through digital channels',
    ],
    compliance: [
      'FAA maintenance, operations, and training records retention',
      'DOT consumer-protection regulations',
      'TSA security-program records retention',
      'PCI DSS — payment card data security for ticket transactions',
      'DGCA regulations — airworthiness and maintenance records (India)',
      'International aviation requirements (IATA, EASA)',
    ],
    operationalBenefits: [
      'Preserved maintenance and flight records post-system migration',
      'Reduced mainframe and specialist costs',
      'Improved passenger dispute and safety response',
      'Enhanced passenger experience through modern digital retailing',
      'Lower distribution costs through direct channel optimization',
    ],
  },
  {
    id: 'industry-construction',
    name: 'Construction',
    shortDesc: 'ERP, project accounting, estimating, scheduling, document control, field, equipment, and payroll systems across construction and engineering firms.',
    iconPath: 'M11.42 15.17L17.25 21A2.652 2.652 0 0021 17.25l-5.877-5.877M11.42 15.17l2.496-3.03c.317-.384.74-.626 1.208-.766M11.42 15.17l-4.655 5.653a2.548 2.548 0 11-3.586-3.586l6.837-5.63m5.108-.233c.55-.164 1.163-.188 1.743-.14a4.5 4.5 0 004.486-6.336l-3.276 3.277a3.004 3.004 0 01-2.25-2.25l3.276-3.276a4.5 4.5 0 00-6.336 4.486c.091 1.076-.071 2.264-.904 2.95l-.102.085m-1.745 1.437L5.909 7.5H4.5L2.25 3.75l1.5-1.5L7.5 4.5v1.409l4.26 4.26m-1.745 1.437l1.745-1.437m6.615 8.206L15.75 15.75M4.867 19.125h.008v.008h-.008v-.008z',
    commonApps: [
      'JD Edwards / Oracle EBS / SAP ECC',
      'Viewpoint Vista',
      'Sage 300 Construction / Timberline',
      'CMiC project management',
      'Oracle Primavera P6',
      'SharePoint 2010 / 2013 (document control)',
      'HCSS field management',
      'Kronos Workforce Central (payroll)',
    ],
    retirementDrivers: [
      'ERP and project platform SaaS adoption',
      'Mergers and regional consolidation',
      'Replacement of file-share and document systems',
      'Aging custom estimating and payroll applications',
      'BIM mandate adoption requiring modern data management',
      'Safety compliance modernization and incident reporting mandates',
    ],
    compliance: [
      'OSHA construction safety records — incidents and worker exposure',
      'Davis-Bacon / certified payroll requirements',
      'FAR government contract records retention',
      'EPA / environmental permits and site remediation records',
      'Warranty, claims, and lien records retention',
      'Contract law — project correspondence and variation records',
    ],
    operationalBenefits: [
      'Preserved completed-project evidence for claims and warranty',
      'Improved claims, warranty, and safety research capabilities',
      'Centralized drawings, RFIs, submittals, and cost records',
      'Reduced rework costs via modern BIM and document management',
      'Better subcontractor management with structured procurement data',
    ],
  },
  {
    id: 'industry-events-services',
    name: 'Events Services',
    shortDesc: 'Event/venue management, registration, ticketing, catering, CRM, finance, and scheduling systems across event organizers and venues.',
    iconPath: 'M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 9v7.5m-9-6h.008v.008H12v-.008zM12 15h.008v.008H12V15zm0 2.25h.008v.008H12v-.008zM9.75 15h.008v.008H9.75V15zm0 2.25h.008v.008H9.75v-.008zM7.5 15h.008v.008H7.5V15zm0 2.25h.008v.008H7.5v-.008zm6.75-4.5h.008v.008h-.008v-.008zm0 2.25h.008v.008h-.008V15zm0 2.25h.008v.008h-.008v-.008zm2.25-4.5h.008v.008H16.5v-.008zm0 2.25h.008v.008H16.5V15z',
    commonApps: [
      'Ungerboeck / Momentus legacy versions',
      'Amadeus Delphi (sales & event management)',
      'Legacy Cvent deployments',
      'Ticketmaster / Archtics',
      'Tessitura / Paciolan',
      'Siebel CRM',
      'Lotus Notes / Domino',
      'Custom event-management systems',
    ],
    retirementDrivers: [
      'Cloud event and venue platform adoption',
      'Ticketing, CRM, and registration system consolidation',
      'Venue acquisitions requiring system integration',
      'Replacement of custom departmental applications',
      'Hybrid and virtual event format requirements post-pandemic',
      'Real-time analytics and ROI measurement requirements',
    ],
    compliance: [
      'PCI DSS — ticket payment data security and retention',
      'State privacy and breach-notification laws',
      'Contract, tax, and revenue records retention',
      'Employment and payroll records retention',
      'Alcohol, food, venue, and safety permitting records',
      'Consumer protection laws — ticket refund and cancellation records',
    ],
    operationalBenefits: [
      'Preserved event and attendee history post-migration',
      'Reduced duplicate ticketing, registration, and CRM systems',
      'Centralized event documents and records',
      'Enhanced attendee experience through modern digital platforms',
      'Improved event ROI measurement with real-time analytics',
    ],
  },
  {
    id: 'industry-automotive',
    name: 'Automotive',
    shortDesc: 'ERP, MES, dealer systems, PLM, quality, warranty, supply-chain, finance, and plant applications across OEMs and dealerships.',
    iconPath: 'M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 00-3.213-9.193 2.056 2.056 0 00-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 00-10.026 0 1.106 1.106 0 00-.987 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12',
    commonApps: [
      'SAP ECC / JD Edwards / Infor LN / Baan',
      'Siemens SIMATIC IT / GE Proficy (MES)',
      'Siemens Teamcenter / PTC Windchill (PLM)',
      'Reynolds & Reynolds ERA / CDK Drive (DMS)',
      'JDA / Blue Yonder (supply chain)',
      'OSIsoft PI (historian)',
      'IBM mainframe / COBOL systems',
      'AS/400 applications',
    ],
    retirementDrivers: [
      'ERP and MES global standardization programs',
      'Plant and dealer-network consolidation',
      'PLM upgrades and digital twin adoption',
      'Warranty and quality transformation initiatives',
      'Electric vehicle (EV) product line requiring modern data platforms',
      'Connected car and OTA software update infrastructure needs',
    ],
    compliance: [
      'NHTSA safety, defect, and recall records retention',
      'EPA emissions compliance — CAFE standards and CO2 reporting',
      'FTC Safeguards Rule — dealer customer data protection',
      'Product liability, warranty, and traceability evidence',
      'OSHA recordkeeping — safety incidents and worker exposure',
      'AIS standards — automotive safety and homologation records (India)',
    ],
    operationalBenefits: [
      'Preserved vehicle, part, warranty, and manufacturing genealogy',
      'Improved recall, warranty, and litigation response',
      'Reduced plant and dealer legacy infrastructure costs',
      'Faster warranty claim resolution with structured historical data',
      'Better recall management with end-to-end vehicle traceability',
    ],
  },
  {
    id: 'industry-entertainment',
    name: 'Entertainment',
    shortDesc: 'Rights, royalties, ticketing, venue, finance, scheduling, distribution, membership, and content-business systems across media and entertainment.',
    iconPath: 'M3.375 19.5h17.25m-17.25 0a1.125 1.125 0 01-1.125-1.125M3.375 19.5h1.5C5.496 19.5 6 18.996 6 18.375m-3.75.125v-5.25A2.25 2.25 0 014.5 11.25h15A2.25 2.25 0 0121.75 13.5v5.25m-18.375.75H21.75m-18.375 0A1.125 1.125 0 013 18.375M21.75 19.5a1.125 1.125 0 001.125-1.125M21.75 19.5h-1.5c-.621 0-1.125-.504-1.125-1.125m3.375.125v-5.25A2.25 2.25 0 0019.5 11.25h-15A2.25 2.25 0 002.25 13.5v5.25',
    commonApps: [
      'Custom rights and royalty platforms',
      'Ticketmaster / Archtics',
      'Tessitura / Ungerboeck / Momentus',
      'SAP ECC / Oracle EBS',
      'Siebel CRM',
      'IBM FileNet / OpenText Documentum',
      'Legacy media asset-management systems',
      'Custom distribution and scheduling systems',
    ],
    retirementDrivers: [
      'Rights, royalty, and distribution platform modernization',
      'Ticketing, CRM, and venue system consolidation',
      'Studio and venue acquisitions',
      'On-premises document and content migration to cloud',
      'OTT and streaming platform migration',
      'AI-driven content recommendation and personalization requirements',
    ],
    compliance: [
      'Copyright and licensing obligations — rights records retention',
      'Union, guild, residual, and talent contract records',
      'PCI DSS — ticket payment data security',
      'State privacy laws — audience and subscriber data',
      'Tax, rights, and financial-audit records',
      'Broadcasting regulations — content and transmission records',
    ],
    operationalBenefits: [
      'Preserved title, rights, royalty, ticketing, and contract history',
      'Reduced duplicate venue and studio systems',
      'Centralized historical rights evidence',
      'Faster content delivery through modern cloud-based pipelines',
      'Better audience insights through unified data platforms',
    ],
  },
  {
    id: 'industry-supermarkets',
    name: 'Supermarkets / Grocery',
    shortDesc: 'POS, merchandising, pricing, inventory, loyalty, pharmacy, replenishment, warehouse, and ERP across grocery and supermarket chains.',
    iconPath: 'M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25a3 3 0 00-3 3h15.75m-12.75-3h11.218c1.121-2.3 2.1-4.684 2.924-7.138a60.114 60.114 0 00-16.536-1.84M7.5 14.25L5.106 5.272M6 20.25a.75.75 0 11-1.5 0 .75.75 0 011.5 0zm12.75 0a.75.75 0 11-1.5 0 .75.75 0 011.5 0z',
    commonApps: [
      'IBM 4690 / Toshiba TCx / NCR legacy POS',
      'Oracle Retail / Retek',
      'JDA / Blue Yonder (merchandising/planning)',
      'Manhattan WMS',
      'Legacy loyalty and coupon databases',
      'Pharmacy management systems',
      'SAP ECC / JD Edwards (ERP)',
      'AS/400 merchandising and inventory systems',
    ],
    retirementDrivers: [
      'POS and store-tech modernization',
      'Digital grocery, loyalty, and e-commerce transformation',
      'Distribution center and ERP standardization',
      'Payment-data risk and PCI DSS scope reduction',
      'Omnichannel grocery and click-and-collect platform requirements',
      'Fresh food waste reduction through demand forecasting adoption',
    ],
    compliance: [
      'PCI DSS — payment card data security and retention',
      'FDA / USDA food-safety and traceability requirements',
      'SNAP / EBT records retention',
      'HIPAA — in-store pharmacy data protection',
      'State privacy, tax, and consumer-protection laws',
      'Weights and measures regulations — pricing accuracy records',
    ],
    operationalBenefits: [
      'Reduced store-server and POS infrastructure costs',
      'Preserved transaction, loyalty, pricing, and pharmacy history',
      'Improved food recall and audit response capabilities',
      'Reduced food waste through modern demand forecasting',
      'Improved shopper loyalty through personalised engagement platforms',
    ],
  },
  {
    id: 'industry-leisure-travel',
    name: 'Leisure / Travel & Tourism',
    shortDesc: 'Reservations, GDS, tour operations, CRM, loyalty, hotel, ticketing, finance, and customer-service systems across travel agencies and tour operators.',
    iconPath: 'M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5',
    commonApps: [
      'Sabre / Amadeus / Travelport Galileo / Apollo / Worldspan',
      'Tourplan (tour operations)',
      'Oracle Hospitality OPERA',
      'Ticketmaster / Archtics',
      'Siebel CRM / PeopleSoft',
      'Custom BSP / ARC settlement systems',
      'Legacy booking and reservation systems',
      'Custom loyalty and customer-service platforms',
    ],
    retirementDrivers: [
      'Reservation and GDS modernization',
      'Cloud travel, hotel, ticketing, and CRM migration',
      'Brand and attraction acquisitions',
      'Replacement of custom booking and settlement applications',
      'Post-pandemic operational restructuring and cost optimization',
      'Sustainability reporting and responsible tourism data requirements',
    ],
    compliance: [
      'PCI DSS — payment card data security for bookings',
      'State and international traveler-data privacy requirements',
      'Seller-of-travel licensing and compliance requirements',
      'IATA / ARC / BSP ticketing and settlement records',
      'Tax, lodging, and occupancy records retention',
      'Consumer protection laws — booking and refund records',
    ],
    operationalBenefits: [
      'Preserved booking and traveler history post-migration',
      'Reduced duplicate GDS, reservation, and back-office systems',
      'Improved refund, dispute, and audit response',
      'Improved customer conversion through modern digital booking platforms',
      'Enhanced traveller loyalty through modern CRM capabilities',
    ],
  },
  {
    id: 'industry-transportation',
    name: 'Transportation / Trucking / Railroad',
    shortDesc: 'TMS, dispatch, fleet, maintenance, ELD, freight, rail, billing, ERP, and mainframe applications across freight, trucking, and rail operators.',
    iconPath: 'M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 00-3.213-9.193 2.056 2.056 0 00-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 00-10.026 0 1.106 1.106 0 00-.987 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12',
    commonApps: [
      'Trimble TMW Suite',
      'McLeod LoadMaster',
      'Oracle Transportation Management',
      'Wabtec / GE RailConnect',
      'IBM z/OS COBOL mainframe',
      'JD Edwards / IBM Maximo',
      'Custom ELD and driver management systems',
      'AS/400 transportation and dispatch systems',
    ],
    retirementDrivers: [
      'TMS, fleet, and mobile platform modernization',
      'Carrier acquisitions and network consolidation',
      'Mainframe and AS/400 dispatch system replacement',
      'Cloud and telematics migration',
      'ELD mandate compliance modernization',
      'Carbon emissions reporting and sustainability requirements',
    ],
    compliance: [
      'FMCSA hours-of-service, ELD, and safety records (US)',
      'FRA rail recordkeeping requirements',
      'DOT / PHMSA hazmat transportation regulations',
      'IFTA / fuel-tax records retention',
      'OSHA recordkeeping — safety incidents',
      'Cargo-claims and freight liability records',
    ],
    operationalBenefits: [
      'Preserved shipment, driver, rail, maintenance, and billing history',
      'Reduced mainframe and TMS infrastructure costs',
      'Simplified acquired carrier system estates',
      'Improved on-time delivery through real-time visibility platforms',
      'Lower compliance risk with automated driver and vehicle records',
    ],
  },
  {
    id: 'industry-it-services',
    name: 'Information Technology & Services',
    shortDesc: 'ERP, CRM, ITSM, PPM, HR, billing, collaboration, knowledge, and custom delivery systems across IT services and technology firms.',
    iconPath: 'M9 17.25v1.007a3 3 0 01-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0115 18.257V17.25m6-12V15a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 15V5.25m18 0A2.25 2.25 0 0018.75 3H5.25A2.25 2.25 0 003 5.25m18 0H3',
    commonApps: [
      'PeopleSoft / Oracle EBS / SAP ECC',
      'Siebel CRM',
      'BMC Remedy / HP Service Manager (ITSM)',
      'CA Clarity PPM',
      'Lotus Notes / Domino',
      'SharePoint 2010 / 2013',
      'HP OpenView / IBM Tivoli (network management)',
      'Custom delivery and billing portals',
    ],
    retirementDrivers: [
      'SaaS modernization of ERP, CRM, ITSM, and HR platforms',
      'Mergers and system standardization',
      'Data-center exit and cloud migration',
      'Replacement of custom portals and legacy applications',
      'Support ticket, project, and billing history retention',
      'Client data governance and security compliance requirements',
    ],
    compliance: [
      'Customer contractual security and data-retention obligations',
      'State privacy laws — client and employee data',
      'HIPAA / GLBA / PCI DSS — in-scope for client data',
      'SOX — financial records for publicly listed firms',
      'Employment and payroll records retention',
      'ISO 27001 — information security records and audit trails',
    ],
    operationalBenefits: [
      'Reduced license and infrastructure overlap',
      'Preserved support tickets, project, customer, and billing history',
      'Consolidated knowledge post-migration',
      'Faster incident resolution through modern ITSM automation',
      'Improved project delivery visibility with modern PPM platforms',
    ],
  },
  {
    id: 'industry-human-resources',
    name: 'Human Resources / Staffing',
    shortDesc: 'HCM, payroll, time, ATS, recruiting, benefits, learning, background-check, and staffing systems across HR departments and HR services firms.',
    iconPath: 'M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z',
    commonApps: [
      'PeopleSoft HCM / SAP HCM / Oracle EBS HRMS',
      'ADP legacy HCM platforms',
      'Kronos / UKG Workforce Central',
      'Oracle Taleo / IBM Kenexa / BrassRing (ATS)',
      'Legacy benefits and pension platforms',
      'Bullhorn / Avionté (staffing)',
      'Ceridian legacy systems',
      'Infor Lawson HR',
    ],
    retirementDrivers: [
      'Cloud HCM and payroll migration (Workday, SAP SuccessFactors)',
      'Post-merger HR system consolidation',
      'High PII exposure from old applicant and employee databases',
      'Multiple redundant ATS, time, and benefits systems',
      'Payroll compliance modernization for new tax and labour regulations',
      'AI-driven talent acquisition and workforce planning adoption',
    ],
    compliance: [
      'FLSA — wage, hour, and payroll records retention',
      'EEOC — personnel and application records retention',
      'OFCCP — federal contractor affirmative action records',
      'Form I-9 / employment verification records',
      'FCRA — background check records retention',
      'State employment and payroll laws',
    ],
    operationalBenefits: [
      'Preserved employment and payroll history post-HCM migration',
      'Reduced employee and applicant PII exposure',
      'Consolidated recruiting, time, and benefits histories',
      'Improved employee experience through modern self-service platforms',
      'Faster talent acquisition with AI-driven recruitment tools',
    ],
  },
  {
    id: 'industry-apparel-fashion',
    name: 'Apparel & Fashion',
    shortDesc: 'ERP, PLM, merchandising, sourcing, planning, WMS, retail, quality, and design systems across apparel brands and fashion retailers.',
    iconPath: 'M9.568 3H5.25A2.25 2.25 0 003 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 005.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 009.568 3z M6 6h.008v.008H6V6z',
    commonApps: [
      'SAP AFS / ECC',
      'JD Edwards / Infor M3 / Lawson',
      'PTC FlexPLM / Centric PLM',
      'Gerber AccuMark / Lectra Modaris / Diamino',
      'JDA / Blue Yonder (planning)',
      'Manhattan WMS',
      'CGS BlueCherry',
      'AS/400 merchandising applications',
    ],
    retirementDrivers: [
      'ERP and PLM modernization programs',
      'Brand acquisitions and sourcing consolidation',
      'Retail, wholesale, and e-commerce convergence',
      'AS/400 dependency reduction',
      'Fast fashion and agile supply chain requirements',
      'Sustainability and ethical sourcing transparency mandates',
    ],
    compliance: [
      'CPSC / CPSIA — product-safety and recall records',
      'FTC textile and labeling requirements',
      'Customs, import, export, and trade-compliance records',
      'PCI DSS and state privacy laws (retail/e-commerce)',
      'Labour and social compliance — supplier audit records',
      'REACH regulations — chemical and material compliance records (EU)',
    ],
    operationalBenefits: [
      'Preserved style, BOM, sourcing, quality, and order history',
      'Reduced duplicate PLM and ERP systems post-acquisition',
      'Improved product-recall and supplier audit response',
      'Faster time-to-market through modern PLM and design platforms',
      'Better sustainability reporting with structured material records',
    ],
  },
  {
    id: 'industry-logistics-supply-chain',
    name: 'Logistics & Supply Chain',
    shortDesc: 'WMS, TMS, OMS, yard, labor, EDI, freight, customs, planning, and ERP systems across 3PL providers and supply chain operators.',
    iconPath: 'M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 00-3.213-9.193 2.056 2.056 0 00-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 00-10.026 0 1.106 1.106 0 00-.987 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12',
    commonApps: [
      'Manhattan Associates WMS',
      'JDA / Blue Yonder WMS',
      'HighJump / Körber legacy versions',
      'Oracle Transportation Management',
      'IBM Sterling Commerce / OMS',
      'IBM Sterling B2B Integrator / Gentran (EDI)',
      'SAP WM / LE',
      'Custom AS/400 logistics systems',
    ],
    retirementDrivers: [
      'Warehouse and TMS modernization and automation',
      'Network consolidation post-acquisition',
      'EDI gateway replacement',
      'Cloud ERP and OMS migration',
      'E-commerce fulfilment automation and robotics integration',
      'Customer SLA requirements for real-time shipment visibility',
    ],
    compliance: [
      'FMCSA / DOT carrier records retention',
      'CBP customs, import, and export compliance',
      'PHMSA hazmat transportation regulations',
      'TSA air-cargo / IATA dangerous-goods requirements',
      'Trade sanctions and export-control compliance',
      'ISO 9001 — quality management records for logistics operations',
    ],
    operationalBenefits: [
      'Preserved order, shipment, warehouse, customs, and partner history',
      'Reduced WMS, TMS, and EDI system duplication',
      'Improved claims, customs, and audit response',
      'Improved order fulfilment accuracy through modern WMS capabilities',
      'Streamlined customs compliance with structured trade records',
    ],
  },
  {
    id: 'industry-media-production',
    name: 'Media Production',
    shortDesc: 'MAM/DAM, newsroom, scheduling, playout, editing, rights, archive, finance, and production-management systems across media production companies.',
    iconPath: 'M3.375 19.5h17.25m-17.25 0a1.125 1.125 0 01-1.125-1.125M3.375 19.5h1.5C5.496 19.5 6 18.996 6 18.375m-3.75.125v-5.25A2.25 2.25 0 014.5 11.25h15A2.25 2.25 0 0121.75 13.5v5.25m-18.375.75H21.75m-18.375 0A1.125 1.125 0 013 18.375M21.75 19.5a1.125 1.125 0 001.125-1.125M21.75 19.5h-1.5c-.621 0-1.125-.504-1.125-1.125m3.375.125v-5.25A2.25 2.25 0 0019.5 11.25h-15A2.25 2.25 0 002.25 13.5v5.25',
    commonApps: [
      'Avid Interplay / MediaCentral',
      'Dalet Galaxy',
      'Grass Valley legacy automation',
      'Harris / Imagine Communications',
      'WideOrbit / ScheduALL (scheduling)',
      'OpenText Documentum',
      'SAP ECC / Oracle EBS (finance/ERP)',
      'Custom rights-management systems',
    ],
    retirementDrivers: [
      'On-premises MAM and broadcast migration to IP/cloud',
      'Studio, channel, and archive consolidation',
      'Proprietary storage replacement',
      'Rights and royalty platform modernization',
      'Remote collaboration requirements for distributed production teams',
      'AI-driven content creation and editing tool adoption',
    ],
    compliance: [
      'Copyright, licensing, rights, and cue-sheet obligations',
      'FCC recordkeeping requirements (regulated broadcasters)',
      'Union, guild, and talent contract records',
      'State privacy laws — crew and talent data',
      'Tax, advertising, and financial records retention',
      'Insurance and liability — production incident documentation',
    ],
    operationalBenefits: [
      'Preserved media metadata and rights evidence post-MAM replacement',
      'Reduced proprietary storage and specialist costs',
      'Centralized historical program, schedule, and asset metadata',
      'Faster production turnaround through modern cloud workflows',
      'Better rights management with structured clearance records',
    ],
  },
  {
    id: 'industry-sports',
    name: 'Sports',
    shortDesc: 'Ticketing, venue, membership, fan CRM, finance, athlete, event, broadcast, and operations systems across sports organizations and leagues.',
    iconPath: 'M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418',
    commonApps: [
      'Ticketmaster / Archtics',
      'Paciolan / Tessitura',
      'Ungerboeck / Momentus (venue management)',
      'Siebel / Microsoft Dynamics CRM',
      'PeopleSoft / Kronos Workforce Central',
      'Stat Crew (statistics)',
      'Legacy video-analysis and digital-asset platforms',
      'Custom ticketing and membership systems',
    ],
    retirementDrivers: [
      'Ticketing, fan CRM, and venue modernization',
      'League, team, or venue mergers and relocations',
      'Custom statistics and athlete system replacement',
      'Cloud migration of media and operational platforms',
      'Real-time athlete performance analytics and wearables integration',
      'Dynamic ticket pricing and revenue optimization needs',
    ],
    compliance: [
      'PCI DSS — ticketing and payment data security',
      'State privacy and breach-notification laws',
      'FERPA — collegiate student-athlete records (US)',
      'HIPAA — athlete health data where applicable',
      'League, CBA, venue, and safety requirements',
      'Anti-doping regulations — athlete testing and records retention',
    ],
    operationalBenefits: [
      'Preserved ticketing, fan, event, and performance history',
      'Reduced duplicate team and venue platforms',
      'Improved customer, league, legal, and audit response',
      'Enhanced fan experience through modern digital engagement platforms',
      'Better revenue optimization via dynamic pricing tools',
    ],
  },
];

// ─── Industry Card ────────────────────────────────────────────────────────────

function IndustryCard({
  industry,
  isHighlighted,
  onClick,
}: {
  industry: IndustryVertical;
  isHighlighted: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="text-left rounded-xl p-5 transition-all duration-200 w-full"
      style={{
        background: isHighlighted ? 'rgba(78,205,196,0.08)' : 'rgba(255,255,255,0.03)',
        border: isHighlighted ? '1.5px solid rgba(78,205,196,0.4)' : '1px solid rgba(255,255,255,0.08)',
      }}
    >
      <div className="flex items-start gap-4">
        <div
          className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{
            background: isHighlighted ? 'rgba(78,205,196,0.15)' : 'rgba(255,255,255,0.05)',
            border: isHighlighted ? '1px solid rgba(78,205,196,0.3)' : '1px solid rgba(255,255,255,0.08)',
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={isHighlighted ? '#4ECDC4' : '#A8B2C1'} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d={industry.iconPath} />
          </svg>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-sm font-bold" style={{ color: isHighlighted ? '#4ECDC4' : '#FFFFFF' }}>
              {industry.name}
            </h3>
            {isHighlighted && (
              <span className="text-xs px-1.5 py-0.5 rounded font-semibold" style={{ background: 'rgba(78,205,196,0.15)', color: '#4ECDC4', fontSize: '9px', letterSpacing: '0.06em' }}>
                YOUR INDUSTRY
              </span>
            )}
          </div>
          <p className="text-xs leading-relaxed" style={{ color: '#A8B2C1' }}>
            {industry.shortDesc}
          </p>
        </div>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#A8B2C1" strokeWidth="2" className="flex-shrink-0 mt-1">
          <polyline points="9 18 15 12 9 6" />
        </svg>
      </div>
    </button>
  );
}

// ─── Industry Detail ──────────────────────────────────────────────────────────

function IndustryDetail({
  industry,
  onBack,
}: {
  industry: IndustryVertical;
  onBack: () => void;
}) {
  const sections: { label: string; items: string[]; color: string }[] = [
    { label: 'Common Legacy Applications', items: industry.commonApps, color: '#FF6B6B' },
    { label: 'Retirement Drivers', items: industry.retirementDrivers, color: '#F59E0B' },
    { label: 'Compliance & Regulatory Requirements', items: industry.compliance, color: '#4ECDC4' },
    { label: 'Operational Benefits of Retirement', items: industry.operationalBenefits, color: '#4ECDC4' },
  ];

  return (
    <div>
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-sm font-medium mb-6 transition-colors"
        style={{ color: '#4ECDC4' }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="15 18 9 12 15 6" />
        </svg>
        Back to Industry Knowledge
      </button>

      <div className="flex items-center gap-4 mb-6">
        <div
          className="w-12 h-12 rounded-xl flex items-center justify-center"
          style={{ background: 'rgba(78,205,196,0.12)', border: '1px solid rgba(78,205,196,0.3)' }}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#4ECDC4" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d={industry.iconPath} />
          </svg>
        </div>
        <div>
          <h1 className="text-2xl font-bold text-white">{industry.name}</h1>
          <p className="text-sm mt-0.5" style={{ color: '#A8B2C1' }}>{industry.shortDesc}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {sections.map((section) => (
          <div
            key={`section-${section.label}`}
            className="rounded-xl p-5"
            style={{ background: '#111F30', border: '1px solid rgba(255,255,255,0.08)' }}
          >
            <h3
              className="text-xs font-bold tracking-widest uppercase mb-4"
              style={{ color: section.color, letterSpacing: '0.08em' }}
            >
              {section.label}
            </h3>
            <ul className="flex flex-col gap-2.5">
              {section.items.map((item, idx) => (
                <li key={`item-${section.label}-${idx}`} className="flex items-start gap-2.5">
                  <div
                    className="w-1.5 h-1.5 rounded-full flex-shrink-0 mt-1.5"
                    style={{ background: section.color, opacity: 0.7 }}
                  />
                  <span className="text-sm leading-relaxed" style={{ color: '#D1D9E3' }}>
                    {item}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Main Grid Component ──────────────────────────────────────────────────────

export default function IndustryGrid() {
  const [selected, setSelected] = useState<IndustryVertical | null>(null);
  const [profileIndustry, setProfileIndustry] = useState<string>('');

  useEffect(() => {
    const raw = localStorage.getItem('smarck_business_profile');
    if (raw) {
      try {
        const profile = JSON.parse(raw);
        if (profile.industry) setProfileIndustry(profile.industry);
      } catch {}
    }
  }, []);

  const sortedIndustries = [...INDUSTRIES].sort((a, b) => {
    const aMatch = a.name === profileIndustry || profileIndustry.startsWith(a.name);
    const bMatch = b.name === profileIndustry || profileIndustry.startsWith(b.name);
    if (aMatch && !bMatch) return -1;
    if (!aMatch && bMatch) return 1;
    return 0;
  });

  if (selected) {
    return <IndustryDetail industry={selected} onBack={() => setSelected(null)} />;
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-xs font-semibold tracking-widest uppercase" style={{ color: '#4ECDC4', letterSpacing: '0.1em' }}>
            REFERENCE CONTENT
          </span>
        </div>
        <h1 className="text-2xl font-bold text-white mb-1">Industry Knowledge</h1>
        <p className="text-sm" style={{ color: '#A8B2C1' }}>
          Legacy application retirement context across 28 enterprise industry verticals. Select an industry to explore common applications, retirement drivers, compliance requirements, and operational benefits.
        </p>
      </div>

      {profileIndustry && (
        <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg mb-5 w-fit" style={{ background: 'rgba(78,205,196,0.08)', border: '1px solid rgba(78,205,196,0.2)' }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#4ECDC4" strokeWidth="2">
            <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <span className="text-xs" style={{ color: '#4ECDC4' }}>
            Showing <strong>{profileIndustry}</strong> first based on your business profile.
          </span>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {sortedIndustries.map((industry) => {
          const isHighlighted =
            industry.name === profileIndustry ||
            profileIndustry.startsWith(industry.name);
          return (
            <IndustryCard
              key={industry.id}
              industry={industry}
              isHighlighted={isHighlighted}
              onClick={() => setSelected(industry)}
            />
          );
        })}
      </div>
    </div>
  );
}